import React, { useEffect } from 'react';
import Container from './Quiz.styl';
import { handleEvent } from '@src/ds/Utils/VideoUtils';

export type VideoProps = {
  authorization?: string;
  mediaCode?: string;
  questions: {
    number: number;
    alternatives: { key: string, value: string }[];
    answer: string;
    description: string;
  }[];
  onClose: () => void;
  opened?: boolean;
};
// eslint-disable-next-line import/no-anonymous-default-export
export default ({
  authorization,
  mediaCode,
  questions,
  onClose,
  opened = false,
}: VideoProps) => {
  const [currentQuestion, setCurrentQuestion] = React.useState<number>(0);
  const [responses, setResponses] = React.useState<(string|null)[]>([]);

  if (!opened) {
    return null;
  }

  const setQuestionResponse = (questionIndex: number, response: string) => {
    const currentResponses = [...responses];
    currentResponses[questionIndex] = response;
    setResponses(currentResponses);

    handleEvent('questionResponse', undefined, {
      questionIndex,
      response,
      isCorrect: response === questions[questionIndex].answer,
    })
  };

  const getResponseClass = (questionIndex: number, response: string) => {
    const correctResponse = questions[questionIndex].answer;

    if (!responses[questionIndex]) {
      return '';
    }

    if (response === correctResponse) {
      return 'quiz__questionAnswers--correct';
    } else if (response === responses[questionIndex]) {
      return 'quiz__questionAnswers--wrong';
    } else {
      return 'quiz__questionAnswers--empty';
    }
  }

  const getCorrectResponses = (): number => {
    return responses.filter((item, index) => item === questions[index].answer).length;
  }

  return (
    <Container className="smartplayer__quiz">
      <div className="quiz__backdrop" onClick={() => onClose()}></div>
      <div className="quiz__container">
        <div className="quiz__heading">
          <h1 className="quiz__title">
            {
              currentQuestion === (questions || []).length ? 'Resultado' : `Quiz - Questão ${currentQuestion + 1}`
            }
          </h1>
          <button type="button" className="quiz__btn quiz__btn--close" onClick={() => onClose()}>
            <svg className="svg-inline--fa fa-xmark" aria-hidden="true" focusable="false" data-prefix="fal" data-icon="xmark" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" data-fa-i2svg="">
              <path fill="currentColor" d="M324.5 411.1c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6L214.6 256 347.1 123.5c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L192 233.4 59.5 100.9c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6L169.4 256 36.9 388.5c-6.2 6.2-6.2 16.4 0 22.6s16.4 6.2 22.6 0L192 278.6 324.5 411.1z"></path>
            </svg>
          </button>
        </div>
        <div className="quiz__content">
          {
            currentQuestion < (questions || []).length ? (
              questions
              .filter((item, index) => index === currentQuestion)
              .map((question) => (
                <div className="quiz__question">
                  <div className="quiz__questionTitle">
                    {question.description}
                  </div>
                  <hr className="quiz_questionHr" />
                  <div className="quiz__questionAnswers">
                    {
                      (question.alternatives || [])?.map((alternative, alternativeIndex) => (
                        <>
                          <input
                            type="radio"
                            name={`q-${question.number}`}
                            id={`q--${question.number}--a--${alternative.key}`}
                            className={`${getResponseClass(question.number, alternative.key)}`}
                            checked={responses[question.number] === alternative.key}
                            onChange={() => setQuestionResponse(question.number, alternative.key)} />
                          <label className="quiz__questionAnswer" htmlFor={`q--${question.number}--a--${alternative.key}`}>
                            {alternative.key}) {alternative.value}
                          </label>
                        </>
                      ))
                    }
                  </div>
                </div>
              ))
            ) : (
              <div className="quiz__question">
                <div className="quiz__questionTitle">
                  Você acertou {getCorrectResponses()} de {responses.length} questões 🎉
                </div>
                <hr className="quiz_questionHr" />
                <div className="quiz__responses">
                  {
                    (questions || []).map((question) => (
                      <div className={'quiz__response ' + getResponseClass(question.number, responses[question.number] || '')}>
                        {question.number + 1}
                      </div>
                    ))
                  }
                </div>
              </div>
            )
          }
        </div>
        <div className="quiz__footer">
          <button className="quiz__btn quiz__btn--left" disabled={currentQuestion === 0} onClick={() => setCurrentQuestion(currentQuestion - 1)}>
            <svg className="svg-inline--fa fa-chevron-left" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-left" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" data-fa-i2svg="" fill="currentColor"><path fill="currentColor" d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"></path></svg>
          </button>
          <div className="quiz__footerCount">
            {
              currentQuestion === (questions || []).length ? '' : `${currentQuestion + 1}/${(questions || []).length}`
            }
          </div>
          <button className="quiz__btn quiz__btn--right" disabled={currentQuestion === (questions || []).length} onClick={() => setCurrentQuestion(currentQuestion + 1)}>
          <svg className="svg-inline--fa fa-chevron-right" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-right" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" data-fa-i2svg="" fill="currentColor"><path fill="currentColor" d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"></path></svg>
          </button>
        </div>
      </div>
    </Container>
  );
};
